<template>
    <div class="delete-cell-modal">
        <DsModal
            center
            show-close
            size="med"
            :is-open="isOpen"
            :title="$t('funnel.cell.delete.title')"
            @close="closeModal"
        >
            <template #header>
                <DsFilledButton
                    data-qa="delete-button"
                    destructive
                    dense
                    @click="confirmDelete"
                >
                    {{ $t('funnel.cell.delete.action') }}
                </DsFilledButton>
            </template>
            <template #default>
                <DsInlineAlert leading-icon class="alert-summary" type="critical">
                    {{ $t('funnel.cell.delete.confirmation') }}
                </DsInlineAlert>
                <span class="warning-summary"> {{ $t('funnel.cell.delete.summary') }} </span>
                <section v-if="showFlowItems">
                    <span class="flow-title"> {{ $t('funnel.cell.delete.section.flow.item') }} </span>
                    <CellInfo
                        v-for="(flowItem, index) in itemsToDeleteInfo.flowItems"
                        :key="`cell-item-${index}`"
                        data-qa="`delete-cell-item-${index}`"
                        :item-type="$options.ITEM_TYPE.FLOW"
                        :cell-type="flowItem.type"
                        :title="flowItem.title"
                        :enabled-integrations="enabledIntegrations"
                        :integration-action-id="flowItem.integrationActionId"
                    />
                </section>
                <section v-if="showGoals">
                    <hr v-if="showFlowItems" class="horizontal-divider" />
                    <span class="flow-title"> {{ $t('funnel.cell.delete.section.goal.uppercase') }} </span>
                    <CellInfo
                        v-for="(goal, index) in itemsToDeleteInfo.goals"
                        :key="`cell-goal-${index}`"
                        :data-qa="`delete-cell-goal-${index}`"
                        :item-type="$options.ITEM_TYPE.GOAL"
                        :cell-type="goal.type"
                        :title="goal.title"
                    />
                </section>
                <section v-if="showDecisions">
                    <hr v-if="showFlowItems || showGoals" class="horizontal-divider" />
                    <span class="flow-title"> {{ $t('funnel.cell.delete.section.decision') }} </span>
                    <CellInfo
                        v-for="(decision, index) in itemsToDeleteInfo.decisions"
                        :key="`cell-decision-${index}`"
                        :data-qa="`delete-cell-decision-${index}`"
                        :item-type="$options.ITEM_TYPE.OTHER"
                        :cell-type="decision.type"
                        :title="decision.title"
                        :icon-src="$options.MESSAGE_CONSTANTS.DECISION_ICON_SRC"
                        :cell-label="$t('funnel.cell.delete.decision.diamond')"
                        :icon-width="$options.MESSAGE_CONSTANTS.DECISION_ICON_WIDTH"
                        :icon-height="$options.MESSAGE_CONSTANTS.DECISION_ICON_HEIGHT"
                        :icon-class="$options.MESSAGE_CONSTANTS.DECISION_ICON_CLASS"
                    />
                </section>
                <section v-if="showFlows" class="flows-container">
                    <hr v-if="showFlowItems || showGoals || showDecisions" class="horizontal-divider" />
                    <span class="flow-title"> {{ $t('funnel.cell.delete.subtitle') }} </span>
                    <div v-for="(flow, key) in itemsToDeleteInfo.flows" :key="`cell-flow-${key}`">
                        <span class="flow-name">
                            {{ flow.title }}
                        </span>
                        <CellInfo
                            v-for="(item, index) in flow.items"
                            :key="`cell-flow-item-${key}-${index}`"
                            :data-qa="`delete-cell-flow-item-${key}-${index}`"
                            :item-type="flow.type"
                            :cell-type="item.type"
                            :title="item.name"
                            :enabled-integrations="enabledIntegrations"
                            :integration-action-id="item.integrationActionId"
                        />
                    </div>
                </section>
                <section v-if="showLeadSources">
                    <hr v-if="showFlowItems || showFlows || showGoals || showDecisions" class="horizontal-divider" />
                    <span class="flow-title"> {{ $t('funnel.cell.delete.section.leadsource') }} </span>
                    <CellInfo
                        v-for="(leadSource, index) in itemsToDeleteInfo.leadSources"
                        :key="`cell-lead-source-${index}`"
                        :data-qa="`delete-cell-lead-source-${index}`"
                        :item-type="$options.ITEM_TYPE.LEAD_SOURCE"
                        :cell-type="leadSource.type"
                        :title="leadSource.title"
                    />
                </section>
                <section v-if="showEdges">
                    <hr v-if="showFlowItems || showFlows || showGoals || showDecisions || showLeadSources" class="horizontal-divider" />
                    <span class="flow-title"> {{ $t('funnel.cell.delete.section.edge') }} </span>
                    <CellInfo
                        v-for="(edge, index) in itemsToDeleteInfo.edges"
                        :key="`cell-edge-${index}`"
                        :data-qa="`delete-cell-edge-${index}`"
                        :item-type="$options.ITEM_TYPE.EDGE"
                        :cell-type="edge.type"
                        :title="edge.title"
                    />
                </section>
            </template>
        </DsModal>
    </div>
</template>

<script>
import CellInfo from '../configuration/cell/CellInfo.vue';
import { findIncomingCells } from '../utils/cell/automation-edge-utils';
import { getCellById } from '../utils/cell/automation-cell-utils';
import {
    GOALS,
    PROCESSES,
    TIMERS,
    FLOW_TYPE,
    DECISION_TYPE,
    LEADSOURCE_TYPE,
    EDGE_TYPE,
    SEQUENCE_START,
    INTEGRATION_ACTION_TYPE,
} from '../utils/editor.constants';

export const ITEM_TYPE = {
    GOAL: 'goal',
    FLOW: 'flow',
    LEAD_SOURCE: 'leadsource',
    EDGE: 'edge',
    NOTE: 'notes',
    OTHER: '',
};

export const MESSAGE_CONSTANTS = {
    DECISION_ICON_SRC: '/resources/funnel/images/svg/decision_small.svg',
    DECISION_ICON_WIDTH: '20',
    DECISION_ICON_HEIGHT: '20',
    DECISION_ICON_CLASS: 'decision-icon-class',
};

export default {
    MESSAGE_CONSTANTS,
    GOALS,
    PROCESSES,
    TIMERS,
    FLOW_TYPE,
    DECISION_TYPE,
    ITEM_TYPE,
    components: {
        CellInfo,
    },
    props: {
        isOpen: {
            type: Boolean,
            required: true,
        },
        elementsToDelete: {
            type: Array,
            required: true,
        },
        cells: {
            type: Array,
            default: () => ([]),
        },
        enabledIntegrations: {
            type: Array,
            default: () => [],
        },
    },
    emits: ['confirm-delete', 'close-delete-dialog'],
    data() {
        return {
            itemsToDelete: {
                leadSourcesData: [],
                goalsData: [],
                flowsData: [],
                flowItemsData: [],
                decisionsData: [],
            },
        };
    },

    computed: {
        showFlows() {
            return this.itemsToDeleteInfo?.flows?.length > 0;
        },

        showFlowItems() {
            return this.itemsToDeleteInfo?.flowItems?.length > 0;
        },

        showGoals() {
            return this.itemsToDeleteInfo?.goals?.length > 0;
        },

        showDecisions() {
            return this.itemsToDeleteInfo?.decisions?.length > 0;
        },

        showEdges() {
            return this.itemsToDeleteInfo?.edges?.length > 0;
        },

        showLeadSources() {
            return this.itemsToDeleteInfo?.leadSources?.length > 0;
        },

        itemsToDeleteInfo() {
            return {
                leadSources: this.processedLeadSources,
                goals: this.processedGoals,
                flows: this.processedFlows,
                flowItems: this.processedFlowItems,
                decisions: this.processedDecisions,
                edges: this.processedEdges,
            };
        },

        processedLeadSources() {
            if (this.itemsToDelete?.leadSourcesData?.length > 0) {
                return this.itemsToDelete.leadSourcesData.map((leadSource) => {
                    return {
                        type: leadSource.type,
                        title: leadSource.value?.name || '',
                    };
                });
            }

            return [];
        },

        processedGoals() {
            if (this.itemsToDelete?.goalsData?.length > 0) {
                return this.itemsToDelete.goalsData.map((goal) => {
                    return {
                        type: goal.type,
                        title: goal.value?.name || '',
                    };
                });
            }

            return [];
        },

        processedFlowItems() {
            if (this.itemsToDelete?.flowItemsData?.length > 0) {
                return this.itemsToDelete.flowItemsData.map((flowItem) => {
                    return {
                        type: flowItem.type,
                        title: flowItem.type === ITEM_TYPE.NOTE ? flowItem.value?.notes : flowItem.value?.name || '',
                        integrationActionId: flowItem.value?.integrationActionId || '',
                    };
                });
            }

            return [];
        },

        processedDecisions() {
            if (this.itemsToDelete?.decisionsData?.length > 0) {
                return this.itemsToDelete.decisionsData.map((decision) => {
                    return {
                        type: decision.type,
                        title: this.getSourceLabel(decision.id),
                    };
                });
            }

            return [];
        },

        processedEdges() {
            if (this.itemsToDelete?.edgesData?.length > 0) {
                return this.itemsToDelete.edgesData.map((edge) => {
                    return {
                        type: edge.type,
                        title: this.getConnectionLabel(edge.meta.source, edge.meta.target),
                    };
                });
            }

            return [];
        },

        processedFlows() {
            if (this.itemsToDelete?.flowsData?.length > 0) {
                return this.itemsToDelete.flowsData.map((flow) => {
                    return {
                        type: flow.type,
                        title: flow.value?.name || '',
                        items: flow.flowItems.map((item) => {
                            return {
                                type: item.type,
                                name: item.value?.name || '',
                                integrationActionId: item.value?.integrationActionId || '',
                            };
                        }),
                    };
                });
            }

            return [];
        },

        innerElements() {
            const response = [];

            this.elementsToDelete.forEach((element) => {
                const matches = this.cells.filter((cell) => cell.meta.parent === element.id
                        && cell.type !== EDGE_TYPE);

                response.push(...matches);
            });

            return response;
        },
    },

    mounted() {
        this.loadData();
    },

    methods: {
        loadData() {
            this.itemsToDelete = {
                leadSourcesData: this.elementsToDelete.filter((element) => {
                    return LEADSOURCE_TYPE === element.type;
                }),
                goalsData: this.elementsToDelete.filter((element) => {
                    return Object.values(GOALS).includes(element.type);
                }),
                flowsData: this.getFlowsData(),
                flowItemsData: this.getFlowItemsData(),
                decisionsData: this.elementsToDelete.filter((element) => {
                    return DECISION_TYPE === element.type;
                }),
                edgesData: this.elementsToDelete.filter((element) => {
                    return EDGE_TYPE === element.type;
                }),
            };
        },

        getConnectionLabel(sourceId, targetId) {
            const sourceElement = getCellById(sourceId, this.cells);
            const targetElement = getCellById(targetId, this.cells);
            const prefix = this.$t('funnel.edit.publish.label.prefix.edge');
            const suffix = this.$t('funnel.edit.publish.label.suffix.edge');
            const response = `${prefix} '${sourceElement.value.name}' ${suffix} '${targetElement.value.name}'`;

            return response;
        },

        getSourceLabel(cellId) {
            const source = findIncomingCells(cellId, this.cells);
            const element = source?.length > 0
                ? source[0]
                : { value: { name: this.$t('funnel.cell.delete.decision.unknown.label') } };

            let label = '';

            if (element?.value?.name) {
                const decisionLabel = this.$t('funnel.cell.delete.decision.label');

                label = `${element?.value?.name}${decisionLabel}`;
            }

            return label;
        },

        getFlowsData() {
            const flows = this.elementsToDelete.filter((element) => {
                return FLOW_TYPE === element.type;
            });

            if (flows?.length > 0) {
                flows.forEach((flow) => {
                    flow.flowItems = this.innerElements.filter((cell) => cell.meta.parent === flow.id);
                });
            }

            return flows;
        },

        getFlowItemsData() {
            const processes = this.elementsToDelete.filter((element) => {
                return [...Object.values(PROCESSES), SEQUENCE_START].includes(element.type);
            });
            const timers = this.elementsToDelete.filter((element) => {
                return Object.values(TIMERS).includes(element.type);
            });
            const integrations = this.elementsToDelete.filter((element) => {
                return element.type === INTEGRATION_ACTION_TYPE;
            });

            return [...processes, ...timers, ...integrations];
        },

        confirmDelete() {
            this.$emit('confirm-delete');
        },

        closeModal() {
            this.$emit('close-delete-dialog');
        },
    },
};
</script>
<style lang="scss" scoped>
.delete-cell-modal {
    width: px-to-rem(600);
}
.warning-summary {
    font-weight: 400;
    font-size: $font-size-md;
    line-height: $line-height-lg;
    color: $color-ink-800;
    padding-bottom: $spacing-200;
}
.alert-summary {
    margin-bottom: $spacing-200;
    align-items: center;
}
.flow-title {
    display: block;
    font-weight: 400;
    font-size: $font-size-lg;
    line-height: $line-height-sm;
    color: $color-ink-800;
    padding-bottom: $spacing-100;
}
.flow-name {
    display: block;
    font-weight: 500;
    font-size: $font-size-sm;
    line-height: $line-height-sm;
    color: $color-ink-800;
    padding-top: $spacing-200;
    padding-bottom: $spacing-100;
}
.horizontal-divider {
    display: block;
    border-bottom: px-to-rem(1) solid $color-ink-200;
    margin-top: $spacing-200;
    margin-bottom: $spacing-200;
}
</style>
